import React, { useState } from "react";
import "./Login.css";
import "./SignUp.css";
import { Link, useNavigate } from "react-router-dom";
import LoginLogo from "../../Components/Assets/images/logo2.png";
import { useTitle } from "../../hooks/useTitle";
import { account, tablesDB, DB_ID, USERS_TABLE } from "../../appwrite";
import { ID, Permission, Role } from "appwrite";
import { CheckCircle, Eye, EyeOff, ChevronRight } from "lucide-react";

function SellerSignup() {
  useTitle("Become a TrendBay Seller");
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0:name 1:email 2:phone 3:password
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+234");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isValidPw = (v) =>
    /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(v);

  const canAdvance = () => {
    if (step === 0) return firstName.trim() && lastName.trim();
    if (step === 1) return isValidEmail(email);
    if (step === 2) return phoneNumber.trim().length >= 7;
    if (step === 3) return isValidPw(password) && password === confirmPassword;
  };

  const advance = () => {
    if (canAdvance() && step < 3) setStep((s) => s + 1);
  };
  const editStep = (s) => {
    setStep(s);
    setError("");
  };

  const normalizePhone = (cc, num) => {
    let clean = num.replace(/\D/g, "");
    if (clean.startsWith("0")) clean = clean.slice(1);
    const cc2 = cc.replace("+", "");
    return clean.startsWith(cc2) ? `+${clean}` : `${cc}${clean}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canAdvance()) return;
    setLoading(true);
    setError("");
    try {
      const fullName = `${firstName} ${lastName}`;
      const userId = ID.unique();
      await account.create(userId, email, password, fullName);
      await account.createEmailPasswordSession(email, password);
      await tablesDB.createRow({
        databaseId: DB_ID,
        tableId: USERS_TABLE,
        rowId: userId,
        data: {
          email,
          fullName,
          phoneNumber: normalizePhone(countryCode, phoneNumber),
          userRole: "seller",
        },
        permissions: [
          Permission.write(Role.user(userId)),
          Permission.read(Role.any()),
        ],
      });
      const otp = String(Math.floor(100000 + Math.random() * 900000));
      const otpExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      await tablesDB.updateRow({
        databaseId: DB_ID, tableId: USERS_TABLE, rowId: userId,
        data: { verifyOtp: otp, verifyOtpExpiry: otpExpiry },
      });
      const WORKER = "https://email-api.trendbayds.com";
      if (WORKER) {
        fetch(`${WORKER}/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "email_verification",
            payload: { email, fullName, otp },
          }),
        }).catch(() => {});
      }
      navigate("/verify-email");
    } catch (err) {
      try {
        await account.deleteSession("current");
      } catch {}
      const msg = err?.message || "";
      if (msg.includes("already exists") || msg.includes("email")) {
        setError("This email is already registered.");
        setStep(1);
      } else {
        setError("Error signing up. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      advance();
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <a href="/">
          <img className="auth-logo" src={LoginLogo} alt="TrendBay" />
        </a>
        <div className="auth-badge">🏪 Seller Portal</div>
        <h1 className="auth-heading">Become a Seller</h1>
        <p className="auth-sub">List & sell your products on TrendBay</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} onKeyDown={onKey}>
          {/* Step 0: Name */}
          {step > 0 ? (
            <div className="signup-done-row" onClick={() => editStep(0)}>
              <div>
                <span className="signup-done-label">Name</span>
                <span className="signup-done-value">
                  {firstName} {lastName}
                </span>
              </div>
              <CheckCircle size={17} color="#00A855" />
            </div>
          ) : (
            <div className="signup-step-fields">
              <div className="auth-name-row">
                <div className="auth-field">
                  <label className="auth-label">First name</label>
                  <input
                    className="auth-input"
                    type="text"
                    placeholder="Ada"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="auth-field">
                  <label className="auth-label">Last name</label>
                  <input
                    className="auth-input"
                    type="text"
                    placeholder="Obi"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <button
                type="button"
                className="signup-continue-btn"
                onClick={advance}
                disabled={!firstName.trim() || !lastName.trim()}
              >
                Continue <ChevronRight size={15} />
              </button>
            </div>
          )}

          {/* Step 1: Email */}
          {step > 1 ? (
            <div className="signup-done-row" onClick={() => editStep(1)}>
              <div>
                <span className="signup-done-label">Email</span>
                <span className="signup-done-value">{email}</span>
              </div>
              <CheckCircle size={17} color="#00A855" />
            </div>
          ) : step === 1 ? (
            <div className="signup-step-fields">
              <div className="auth-field">
                <label className="auth-label">Email address</label>
                <input
                  className="auth-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <button
                type="button"
                className="signup-continue-btn"
                onClick={advance}
                disabled={!isValidEmail(email)}
              >
                Continue <ChevronRight size={15} />
              </button>
            </div>
          ) : null}

          {/* Step 2: Phone */}
          {step > 2 ? (
            <div className="signup-done-row" onClick={() => editStep(2)}>
              <div>
                <span className="signup-done-label">Phone</span>
                <span className="signup-done-value">
                  {countryCode} {phoneNumber}
                </span>
              </div>
              <CheckCircle size={17} color="#00A855" />
            </div>
          ) : step === 2 ? (
            <div className="signup-step-fields">
              <div className="auth-field">
                <label className="auth-label">Phone number</label>
                <div className="auth-phone-row">
                  <select
                    className="auth-phone-cc"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+234">🇳🇬 +234</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+1">🇺🇸 +1</option>
                  </select>
                  <input
                    className="auth-input"
                    type="text"
                    placeholder="08012345678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <button
                type="button"
                className="signup-continue-btn"
                onClick={advance}
                disabled={phoneNumber.trim().length < 7}
              >
                Continue <ChevronRight size={15} />
              </button>
            </div>
          ) : null}

          {/* Step 3: Password */}
          {step === 3 && (
            <div className="signup-step-fields">
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <div className="auth-pw-wrap">
                  <input
                    className="auth-input"
                    type={showPw ? "text" : "password"}
                    placeholder="Min. 8 chars, 1 uppercase, 1 number, 1 symbol"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                  />
                  <span
                    className="auth-pw-toggle"
                    onClick={() => setShowPw((p) => !p)}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </span>
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">Confirm password</label>
                <div className="auth-pw-wrap">
                  <input
                    className="auth-input"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <span
                    className="auth-pw-toggle"
                    onClick={() => setShowConfirm((p) => !p)}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </span>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="signup-pw-mismatch">Passwords don't match</p>
                )}
              </div>
              <button
                type="submit"
                className="auth-btn-primary"
                disabled={
                  loading ||
                  !isValidPw(password) ||
                  password !== confirmPassword
                }
              >
                {loading ? "Creating account…" : "Create Seller Account"}
              </button>
            </div>
          )}
        </form>

        <div className="auth-divider">already have a seller account?</div>
        <Link to="/login" className="auth-btn-outline">
          Sign In
        </Link>

        <p className="auth-footer-text">
          By signing up you agree to our{" "}
          <a href="https://mart.trendbayds.com/terms-and-conditions">
            Terms &amp; Conditions
          </a>{" "}
          and{" "}
          <a href="https://mart.trendbayds.com/privacy-policy">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}

export default SellerSignup;
